import React, {useMemo} from 'react';
import {Button, FlexItem, FlexRow, Heading, Table, Text, theme} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import type {SettingsClientInformation_viewer$data as SettingsClientInformation_viewer} from './__generated__/SettingsClientInformation_viewer.graphql';
import Util from '../../shared/util/util';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';

import InvoicingIntegrationIcon from '../finance-page/InvoicingIntegrationIcon';

const BillToSectionContainer = Styled.div`
	width: 100%;
	border-left: 1px solid ${theme.colors.border.separator};
	border-right: 1px solid ${theme.colors.border.separator};
	margin-bottom: 25px;
	border-radius: ${theme.border.radius['2']};
	border-top: 1px solid ${theme.colors.border.separator};
`;

const BillFromEntityBar = Styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	border-bottom: 1px solid ${theme.colors.border.separator};
	align-items: center;
	padding-left: ${theme.spacing[400]};
	padding-top: ${theme.spacing[600]};
	padding-bottom: ${theme.spacing[600]};
`;

const BillToTableWrapper = Styled.div`
	width: 100%;
	height: 100%;
`;

const TableBarWrapper = Styled.div`
	margin-top: 32px;
	margin-bottom: 15px;
`;

const NoBillToEntitiesContainer = Styled.div`;
	height: 200px;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: ${theme.border.radius['2']};
	border: 1px solid ${theme.colors.border.separator};
`;

type SettingsClientIntegrationsProps = {
	viewer: SettingsClientInformation_viewer;
};

type formattedBillTo = {
	address: string;
	billFromId: string;
	clientBillToId: string;
	id: string;
	name: string;
	taxId: string;
};

const mappedBillFroms = (viewer: SettingsClientInformation_viewer) => viewer.company?.billFroms;

const mappedClientBillTos = (viewer: SettingsClientInformation_viewer) =>
	viewer.company?.client?.clientBillTos?.edges?.map(clientBillTo => clientBillTo?.node);

const constructBillTos = (billFrom, clientBillTos) => {
	const billTos: formattedBillTo[] = [];
	const filteredClientBillTos = clientBillTos?.filter(clientBillTo => clientBillTo?.billFrom?.id === billFrom?.id);
	for (const clientBillTo of filteredClientBillTos) {
		if (clientBillTo) {
			const obj = {
				clientBillToId: clientBillTo.id,
				address: clientBillTo.billTo.address,
				name: clientBillTo.billTo.name,
				taxId: clientBillTo.billTo.taxId,
				id: clientBillTo.billTo.id,
				invoiced: clientBillTo.invoiced,
				billFromId: clientBillTo.billFrom.id,
			};
			billTos.push(obj);
		}
	}

	return billTos;
};

const formatData = (billFroms, clientBillTos) =>
	billFroms
		?.map(billFrom => ({
			billFrom: billFrom,
			billTos: constructBillTos(billFrom, clientBillTos),
		}))
		.filter(data => data.billTos?.length !== 0); // If the bill to array is empty, it means this client has no bill to entity for a specific bill from

function SettingsClientAssociatedBillTosSection({viewer}: SettingsClientIntegrationsProps): JSX.Element {
	const {formatMessage} = useIntl();
	const billFroms = useMemo(() => mappedBillFroms(viewer), []); // All the available bill froms. We need this for the bill from info. Name and integration.
	const clientBillTos = useMemo(() => mappedClientBillTos(viewer), [viewer]); // These are the associations from the client to the bill to entities.
	const formattedData = useMemo(() => formatData(billFroms, clientBillTos), [viewer]);
	const clientId = viewer.company?.client?.id;
	const hasIntegrationEnabled =
		viewer.company?.economicEnabled || viewer.company?.newXeroEnabled || viewer.company?.quickbooksEnabled;

	const unAssociateBillToEntity = row => {
		if (row.invoiced) {
			showModal({
				type: MODAL_TYPE.INVOICED_BILL_TO_WARNING,
				billToId: row.id,
				clientId,
			});
		} else {
			showModal({
				type: MODAL_TYPE.REMOVE_CLIENT_BILL_TO,
				clientBillToId: row.clientBillToId,
				clientId,
			});
		}
	};

	const associateNewBillToEntity = () => {
		showModal({
			type: MODAL_TYPE.ASSOCIATE_BILL_TO_ENTITY,
			clientId,
		});
	};

	return (
		<>
			<TableBarWrapper>
				<FlexRow justifyContent={'space-between'} gap={'s'}>
					<Heading size={'4'}>{"Associated 'Bill To' entities"}</Heading>
					{hasIntegrationEnabled && (
						<Button emphasis={'low'} onPress={() => associateNewBillToEntity()}>
							{"Associate a new 'Bill To' entity"}
						</Button>
					)}
				</FlexRow>
			</TableBarWrapper>

			{formattedData.length > 0 ? (
				formattedData?.map(data => {
					return (
						<BillToSectionContainer>
							<BillFromEntityBar>
								<FlexRow gap={'l'}>
									<FlexItem>
										<InvoicingIntegrationIcon integration={data?.billFrom?.integration} />
									</FlexItem>
									<FlexItem>{data?.billFrom?.name}</FlexItem>
									<FlexItem>({data?.billTos?.length})</FlexItem>
								</FlexRow>
							</BillFromEntityBar>
							<FlexRow>
								<BillToTableWrapper>
									<Table data={data?.billTos} width={'100%'}>
										<Table.TextColumn
											accessorKey={'name'}
											header={formatMessage({id: 'settings_finance.bill_to_entity_name'})}
											width={'350px'}
										/>
										<Table.TextColumn
											accessorKey={'address'}
											header={formatMessage({id: 'settings_finance.address'})}
										/>
										{Util.shouldDisplayTaxIdColumn(data?.billFrom?.integration) && (
											<Table.TextColumn
												accessorKey={'taxId'}
												header={formatMessage({id: 'settings.client.vat_number'})}
												width={'200px'}
											/>
										)}
										<Table.ButtonColumn
											align={Table.Align.right}
											header={formatMessage({id: 'settings_finance.un_associate_button'})}
											hideHeader={true}
											button={row => ({
												label: 'Un-associate',
												onClick: () => unAssociateBillToEntity(row),
											})}
											width={'150px'}
										/>
									</Table>
								</BillToTableWrapper>
							</FlexRow>
						</BillToSectionContainer>
					);
				})
			) : (
				<NoBillToEntitiesContainer>
					<Text color={'default'}>
						{formatMessage({id: 'settings_finance.no_bill_to_entities_container_description'})}
					</Text>
				</NoBillToEntitiesContainer>
			)}
		</>
	);
}

export default SettingsClientAssociatedBillTosSection;
